import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, Tab, Paper, Typography, Box } from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import {
  TenantConfig,
  TenantConfiguratorOptions,
  TenantConfiguratorVariant,
} from './types';
import { createDefaultConfig, createDefaultEnvironment, computeExpiresDate } from './defaults';
import { useApiCatalog } from './useApiCatalog';
import { useBusinessUnits } from './useBusinessUnits';
import { useRepoEnvironments } from './useRepoEnvironments';
import { GeneralTab } from './GeneralTab';
import { OwnershipTab } from './OwnershipTab';
import { EnvironmentsTab } from './EnvironmentsTab';
import { ApisTab } from './ApisTab';
import { LabelsTopicsTab } from './LabelsTopicsTab';
import { AdvancedTab } from './AdvancedTab';
import { ArtifactRegistryTab } from './ArtifactRegistryTab';
import { TerraformTab } from './TerraformTab';
import { EditFilePicker } from './EditFilePicker';

export type { TenantConfiguratorOptions } from './types';

interface TabDef {
  id: string;
  label: string;
  variants: TenantConfiguratorVariant[];
}

const ALL_TABS: TabDef[] = [
  { id: 'general', label: 'General', variants: ['default', 'sandbox', 'edit'] },
  { id: 'ownership', label: 'Ownership', variants: ['default', 'sandbox', 'edit'] },
  { id: 'environments', label: 'Environments', variants: ['default', 'edit'] },
  { id: 'apis', label: 'APIs', variants: ['default', 'sandbox', 'edit'] },
  { id: 'labels', label: 'Labels & Topics', variants: ['default', 'sandbox', 'edit'] },
  { id: 'gar', label: 'Artifact Registry', variants: ['default', 'sandbox', 'edit'] },
  { id: 'advanced', label: 'Advanced', variants: ['default', 'edit'] },
  { id: 'terraform', label: 'Terraform', variants: ['default', 'edit'] },
];

export const TenantConfigurator = (
  props: FieldExtensionComponentProps<object, TenantConfiguratorOptions>,
) => {
  const { onChange, uiSchema, formData: initialFormData } = props;

  const options = uiSchema?.['ui:options'];
  const identityApi = useApi(identityApiRef);
  const owner = options?.owner || 'badal-io';
  const variant: TenantConfiguratorVariant = (options?.variant as TenantConfiguratorVariant) || 'default';
  const apiCatalogRepo = options?.apiCatalogRepo || 'backstage-templates';
  const apiCatalogPath = options?.apiCatalogPath || 'shared/apis.yaml';
  const restrictedApiCatalogPath = options?.restrictedApiCatalogPath || 'shared/restricted-apis.yaml';
  const businessUnitCatalogPath = options?.businessUnitCatalogPath || 'shared/business-units.yaml';
  const apiCatalogRef = options?.apiCatalogRef;

  // Filter tabs by variant
  const visibleTabs = useMemo(
    () => ALL_TABS.filter(t => t.variants.includes(variant)),
    [variant],
  );

  const SANDBOX_BU_REPO = 'bu-sandbox-h8fo9';

  // Support pre-filling via URL query parameter: ?formData={"tenant_config":{"business_unit_repo":"..."}}
  const prefill = (initialFormData && typeof initialFormData === 'object')
    ? initialFormData as Partial<TenantConfig>
    : {};

  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState<TenantConfig>(() => {
    const defaults = createDefaultConfig();
    if (variant === 'sandbox') {
      return { ...defaults, project_end_months: 3, project_end_date: computeExpiresDate(3), project_expiry: computeExpiresDate(3), purpose: 'sandbox', business_unit_repo: SANDBOX_BU_REPO, ...prefill };
    }
    return { ...defaults, ...prefill };
  });

  // Edit mode: track selected file and whether config has been loaded
  const [editFile, setEditFile] = useState<string>('');
  const [editFileLoaded, setEditFileLoaded] = useState(false);

  const { categories, restrictedCategories, loading: catalogLoading, error: catalogError } = useApiCatalog(
    owner,
    apiCatalogRepo,
    apiCatalogPath,
    apiCatalogRef as string | undefined,
    variant !== 'sandbox' ? (restrictedApiCatalogPath as string | undefined) : undefined,
  );

  const { businessUnits } = useBusinessUnits(
    owner,
    apiCatalogRepo,
    businessUnitCatalogPath as string,
    apiCatalogRef as string | undefined,
  );

  const { availableEnvironments, defaultBudget, defaultLifetimeBudget } = useRepoEnvironments(
    owner,
    config.business_unit_repo || undefined,
  );

  // Auto-populate environments and budget when BU changes (not in edit mode after file loaded)
  useEffect(() => {
    if (variant === 'edit' && editFileLoaded) return;
    if (availableEnvironments.length > 0) {
      const defaultEnvNames = availableEnvironments.filter(
        name => name.toLowerCase() !== 'production',
      );
      const newEnvironments = defaultEnvNames.map(name => ({
        ...createDefaultEnvironment(),
        name,
      }));
      setConfig(prev => {
        const updates: Partial<TenantConfig> = { environments: newEnvironments };
        if (variant !== 'edit') {
          if (defaultLifetimeBudget !== null) {
            updates.lifetime_budget = defaultLifetimeBudget;
            updates.budget_amount = Math.round(defaultLifetimeBudget / prev.project_end_months);
          } else if (defaultBudget !== null) {
            updates.budget_amount = defaultBudget;
            updates.lifetime_budget = defaultBudget * prev.project_end_months;
          }
          setLifetimeBudgetOverridden(false);
        }
        return { ...prev, ...updates };
      });
    }
  }, [availableEnvironments, defaultBudget, defaultLifetimeBudget, variant, editFileLoaded]);

  // Sandbox: auto-fill description with username from Backstage identity
  useEffect(() => {
    if (variant !== 'sandbox') return;
    const loadIdentity = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const username = identity.userEntityRef?.split('/').pop() || 'user';
        setConfig(prev => {
          const updates: Partial<TenantConfig> = {};
          if (!prev.description) {
            updates.description = `Sandbox for ${username}`;
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      } catch {
        // Identity not available
      }
    };
    loadIdentity();
  }, [variant, identityApi]);

  const restrictedApiSet = useMemo(() => {
    const s = new Set<string>();
    for (const cat of restrictedCategories) {
      for (const item of cat.items) {
        s.add(item.api);
      }
    }
    return s;
  }, [restrictedCategories]);

  // Form-wide validation
  const ownershipValid = useMemo(() => {
    const hasMaintainer = config.teams.some(
      t => t.name && t.permission === 'maintain',
    );
    if (variant === 'sandbox') return hasMaintainer;
    return !!(config.purpose && config.owner_name && config.owner_email && hasMaintainer);
  }, [variant, config.purpose, config.owner_name, config.owner_email, config.teams]);

  const generalValid = useMemo(() => {
    const budgetOk = variant === 'sandbox' || (config.lifetime_budget > 100 && config.budget_amount > 10);
    return !!(config.tenant_name && config.description && config.business_unit_repo && budgetOk);
  }, [config.tenant_name, config.description, config.business_unit_repo, config.lifetime_budget, config.budget_amount, variant]);

  const formValid = ownershipValid && generalValid;

  // Propagate config changes to the scaffolder form
  useEffect(() => {
    const output: Record<string, unknown> = { ...config };
    if (variant === 'edit' && editFile) {
      output._editFile = editFile;
    }
    output._variant = variant;
    output._ownershipValid = ownershipValid;
    output._formValid = formValid;
    onChange(output as unknown as object);
  }, [config, onChange, variant, editFile, ownershipValid, formValid]);

  const [lifetimeBudgetOverridden, setLifetimeBudgetOverridden] = useState(false);

  const handleConfigChange = useCallback((newConfig: TenantConfig) => {
    // Sandbox auto-defaults: triggered when purpose changes to 'sandbox' or BU changes to sandbox
    const purposeChangedToSandbox = newConfig.purpose === 'sandbox' && config.purpose !== 'sandbox';
    const buChangedToSandbox = newConfig.business_unit_repo === SANDBOX_BU_REPO && config.business_unit_repo !== SANDBOX_BU_REPO;

    if (purposeChangedToSandbox || buChangedToSandbox) {
      const expiresDate = computeExpiresDate(3);
      newConfig = {
        ...newConfig,
        purpose: 'sandbox',
        business_unit_repo: SANDBOX_BU_REPO,
        lifetime_budget: 600,
        budget_amount: 200,
        project_end_months: 3,
        project_end_date: expiresDate,
        project_expiry: expiresDate,
        environments: [{ ...createDefaultEnvironment(), name: 'development' }],
      };
      setLifetimeBudgetOverridden(false);
    }

    if (newConfig.project_end_months !== config.project_end_months) {
      const expiresDate = computeExpiresDate(newConfig.project_end_months);
      newConfig = {
        ...newConfig,
        project_end_date: expiresDate,
        project_expiry: expiresDate,
      };
    }

    if (!lifetimeBudgetOverridden && variant !== 'edit') {
      const newMonthly = newConfig.project_end_months > 0
        ? Math.round(newConfig.lifetime_budget / newConfig.project_end_months)
        : newConfig.lifetime_budget;
      if (newConfig.budget_amount !== newMonthly) {
        newConfig = { ...newConfig, budget_amount: newMonthly };
      }
    }

    // Special-approval logic (not for sandbox)
    if (variant !== 'sandbox') {
      const hasRestrictedApis = newConfig.additional_apis.some(api => restrictedApiSet.has(api));
      const hasSpecialApprovalTopic = newConfig.topics.includes('special-approval');
      const hasSpecialApprovalLabel = newConfig.labels.some(l => l.key === 'special-approval');

      if (hasRestrictedApis && !hasSpecialApprovalTopic) {
        newConfig = { ...newConfig, topics: [...newConfig.topics, 'special-approval'] };
      } else if (!hasRestrictedApis && hasSpecialApprovalTopic) {
        newConfig = { ...newConfig, topics: newConfig.topics.filter(t => t !== 'special-approval') };
      }

      if (hasRestrictedApis && !hasSpecialApprovalLabel) {
        newConfig = {
          ...newConfig,
          labels: [...newConfig.labels, { key: 'special-approval', value: 'true' }],
        };
      } else if (!hasRestrictedApis && hasSpecialApprovalLabel) {
        newConfig = {
          ...newConfig,
          labels: newConfig.labels.filter(l => l.key !== 'special-approval'),
        };
      }
    }

    const updatedEnvironments = newConfig.environments.map(env => ({
      ...env,
      additional_apis: newConfig.additional_apis,
      labels: newConfig.labels,
    }));

    setConfig({
      ...newConfig,
      environments: updatedEnvironments,
    });
  }, [config.project_end_months, config.purpose, config.business_unit_repo, lifetimeBudgetOverridden, restrictedApiSet, variant]);

  // Handle edit mode file loaded
  const handleEditConfigLoaded = useCallback((loadedConfig: TenantConfig, file: string) => {
    setConfig(loadedConfig);
    setEditFile(file);
    setEditFileLoaded(true);
  }, []);

  const renderTabContent = (tabId: string) => {
    switch (tabId) {
      case 'general':
        return (
          <GeneralTab
            config={config}
            onChange={handleConfigChange}
            variant={variant}
            businessUnits={businessUnits}
            onLifetimeBudgetOverride={() => setLifetimeBudgetOverridden(true)}
            lifetimeBudgetOverridden={lifetimeBudgetOverridden}
            buLocked={variant === 'sandbox' || (variant === 'edit' && editFileLoaded)}
          />
        );
      case 'ownership':
        return (
          <OwnershipTab
            config={config}
            onChange={handleConfigChange}
            owner={owner}
            variant={variant}
          />
        );
      case 'environments':
        return (
          <EnvironmentsTab
            config={config}
            onChange={handleConfigChange}
            availableEnvironments={availableEnvironments}
          />
        );
      case 'apis':
        return catalogLoading ? (
          <Typography>Loading API catalog...</Typography>
        ) : catalogError ? (
          <Typography color="error">{catalogError}</Typography>
        ) : (
          <ApisTab
            config={config}
            onChange={handleConfigChange}
            categories={categories}
            restrictedCategories={variant === 'sandbox' ? [] : restrictedCategories}
            variant={variant}
          />
        );
      case 'labels':
        return <LabelsTopicsTab config={config} onChange={handleConfigChange} />;
      case 'gar':
        return <ArtifactRegistryTab config={config} onChange={handleConfigChange} />;
      case 'advanced':
        return (
          <AdvancedTab config={config} onChange={handleConfigChange} owner={owner} />
        );
      case 'terraform':
        return <TerraformTab config={config} onChange={handleConfigChange} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Edit mode: file picker header */}
      {variant === 'edit' && !editFileLoaded && (
        <EditFilePicker
          owner={owner}
          config={config}
          businessUnits={businessUnits}
          onBuChange={(repo) => setConfig(prev => ({ ...prev, business_unit_repo: repo }))}
          onConfigLoaded={handleEditConfigLoaded}
        />
      )}

      {/* Show tabs when not in edit mode waiting for file selection */}
      {(variant !== 'edit' || editFileLoaded) && (
        <Paper variant="outlined" style={{ display: 'flex', minHeight: 500 }}>
          <Tabs
            orientation="vertical"
            value={activeTab}
            onChange={(_e, newValue) => setActiveTab(newValue)}
            style={{
              borderRight: '1px solid #e0e0e0',
              minWidth: 180,
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            {visibleTabs.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.label}
                style={{
                  textTransform: 'none',
                  alignItems: 'flex-start',
                  ...((tab.id === 'ownership' && !ownershipValid) || (tab.id === 'general' && !generalValid)
                    ? { color: '#d32f2f', fontWeight: 'bold' }
                    : {}),
                }}
              />
            ))}
          </Tabs>

          <Box flex={1} p={3} style={{ overflow: 'auto' }}>
            {renderTabContent(visibleTabs[activeTab]?.id)}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default TenantConfigurator;
